import {getGridNumericOperators, GridColDef, GridColumnHeaderParams, GridValueFormatterParams} from "@mui/x-data-grid";
import {GridApiCommunity} from "@mui/x-data-grid/internals";
import {MutableRefObject} from "react";

import {DetailType} from "../../types/enums";
import {Correlation, Enrichment, Score} from "../../types/list";
import {useGeneCellDataFilters} from "./GeneCellDataFilters";
import ListHeaderFiltered from "./ListHeaderFiltered";
import ListRenderDetail from "./ListRenderDetail";
import ListRenderGene from "./ListRenderGene";


// Formatters
const formatDecimalValue = (fractionDigits: number = 4) => {
  return (params: GridValueFormatterParams<number>): string => {
    return params.value == null ? "" : params.value.toFixed(fractionDigits);
  }
}

const formatExponentialValue = (fractionDigits: number = 1) => {
  return (params: GridValueFormatterParams<number>): string => {
    return params.value == null ? "" : params.value.toExponential(fractionDigits);
  }
}

function formatSuppressionScore(params: GridValueFormatterParams<number>): string {
  if (params.value == null || params.value === 0) return "—";
  return params.value.toFixed(4);
}

export const useQGiColumns = (apiRef: MutableRefObject<GridApiCommunity>) => {
  const renderFilterableHeader = (params: GridColumnHeaderParams<Score>) =>
    <ListHeaderFiltered apiRef={apiRef} name={params.colDef.headerName} field={params.field}/>;
  const filterOperators = useGeneCellDataFilters();

  const scoreColumns: GridColDef<Score>[] = [
    {
      field: "screen_cell",
      headerName: "Query Gene",
      headerClassName: "list-header",
      flex: 2,
      minWidth: 180,
      renderCell: (params) =>
        <ListRenderGene<Score> params={params}/>,
      renderHeader: renderFilterableHeader,
      valueGetter: params => params.row.screen_cell.display,
      filterOperators: filterOperators,
    },
    {
      field: "library_cell",
      headerName: "Library Gene",
      headerClassName: "list-header",
      flex: 2,
      minWidth: 180,
      renderCell: (params) =>
        <ListRenderGene<Score> params={params}/>,
      renderHeader: renderFilterableHeader,
      valueGetter: params => params.row.library_cell.display,
      filterOperators: filterOperators,
    },
    {
      field: "qgi",
      headerName: "qGI",
      headerClassName: "list-header",
      flex: 1,
      minWidth: 95,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueFormatter: formatDecimalValue(4),
    },
    {
      field: "fdr",
      headerName: "FDR",
      headerClassName: "list-header",
      flex: 1,
      minWidth: 90,
      valueFormatter: formatExponentialValue(1),
    }
  ];

  const suppressionCol: GridColDef = {
    field: "suppression_score",
    headerName: "Suppression",
    headerClassName: "list-header",
    flex: 1,
    minWidth: 120,
    valueFormatter: formatSuppressionScore,
  }

  const detailCol: GridColDef<Score> = {
    field: DetailType.QGI,
    headerName: "",
    headerClassName: "list-header",
    width: 80,
    minWidth: 80,
    maxWidth: 80,
    sortable: false,
    renderCell: (params) => <ListRenderDetail<Score> params={params}/>,
  }

  return {
    negativeColumns: [...scoreColumns, detailCol],
    positiveColumns: [...scoreColumns, suppressionCol, detailCol]
  };
};

export const useCorrelationColumns = (apiRef: MutableRefObject<GridApiCommunity>) => {
  const renderFilterableHeader = (params: GridColumnHeaderParams<Correlation>) =>
    <ListHeaderFiltered apiRef={apiRef} name={params.colDef.headerName} field={params.field}/>;
  const filterOperators = useGeneCellDataFilters();

  const correlationColumns: GridColDef<Correlation>[] = [
    {
      field: "g1_cell",
      headerName: "Library Gene (A)",
      headerClassName: "list-header",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => <ListRenderGene<Correlation> params={params}/>,
      renderHeader: renderFilterableHeader,
      valueGetter: params => params.row.g1_cell.display,
      filterOperators: filterOperators,
    },
    {
      field: "g2_cell",
      headerName: "Library Gene (B)",
      headerClassName: "list-header",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => <ListRenderGene<Correlation> params={params}/>,
      renderHeader: renderFilterableHeader,
      valueGetter: params => params.row.g2_cell.display,
      filterOperators: filterOperators,
    },
    {
      field: "pcc",
      headerName: "Profile Similarity (PCC)",
      headerClassName: "list-header",
      flex: 1,
      minWidth: 170,
      valueFormatter: formatDecimalValue(4),
      filterOperators: getGridNumericOperators().filter((op) =>
        ['>', '<', '>=', '<='].includes(op.value)
      ),
    },
    {
      field: DetailType.PCC,
      headerName: "",
      headerClassName: "list-header",
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      sortable: false,
      renderCell: (params) => <ListRenderDetail<Correlation> params={params}/>,
    }
  ]

  return {correlationColumns};
}

export const useEnrichmentColumns = () => {
  const enrichmentColumns: GridColDef<Enrichment>[] = [
    {
      field: "name",
      headerName: "Term",
      flex: 8,
      minWidth: 200,
      headerClassName: "list-header",
      cellClassName: "enrichment-table-cell",
    },
    {
      field: "id",
      headerName: "Term ID",
      flex: 3,
      minWidth: 120,
      align: "left",
      headerAlign: "left",
      headerClassName: "list-header",
      disableColumnMenu: true,
    },
    {
      field: "pval",
      headerName: "P-value",
      type: "number",
      flex: 1,
      minWidth: 90,
      align: "left",
      headerAlign: "left",
      headerClassName: "list-header",
      valueFormatter: formatExponentialValue(2),
    },
    {
      field: "fdr",
      headerName: "FDR",
      type: "number",
      flex: 1,
      minWidth: 90,
      align: "left",
      headerAlign: "left",
      headerClassName: "list-header",
      valueFormatter: formatExponentialValue(2),
    },
    {
      field: "fold_enrichment",
      headerName: "Fold Enrich",
      type: "number",
      flex: 1,
      minWidth: 120,
      align: "left",
      headerAlign: "left",
      headerClassName: "list-header",
      valueFormatter: formatDecimalValue(2),
    },
    {
      field: "gene_count",
      headerName: "k / K",
      flex: 1,
      minWidth: 90,
      align: "left",
      headerAlign: "left",
      headerClassName: "list-header",
      valueGetter: (params) => `${params.row.k}/${params.row.K}`,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
  ];

  return {enrichmentColumns};
}
